import React, { useMemo, useRef } from 'react';
import { BaseComponentProps } from '@/pages/benchmark/types/commom';
import classNames from 'classnames';
import CardTitleBox from '@home/pages/studio/components/CardTitleBox';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import useScrollbar from '@home/pages/studio/socialIndex/components/CustomTable/useScrollbar';
import { getCompletionRender, getGameRender, getWeekRender } from './getRender';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { dynamicString } from '@home/utils/dynamicString';
import moment from 'moment';
import './index.scss';

interface IColumn {
  nameEn: string;
  nameCn: string;
  key: string;
  width?: string;
  lastWeekNameEn?: string;
  lastWeekNameCn?: string;
  lastWeekKey?: string;
  lastTwoWeekKey?: string;
  lastTwoWeekNameEn?: string;
  lastTwoWeekNameCn?: string;
  kpiNameEn?: string;
  kpiNameCn?: string;
  lastWeekColor?: string;
  lastTwoWeekColor?: string;
  kpiColor?: string;
  valueFormatRule?: string;
  percentFormatRule?: string;
  render?: string | Function;
  align?: "center" | "left" | "right" | "justify" | "char";
  className?: string;
  formatRule?: string;
  dateFormatRule?: string;
}

interface IKIPTableCardProps extends BaseComponentProps {
  titleEn: string;
  titleCn: string;
  columns: IColumn[];
  tableData: any[];
  [key: string]: any;
}

const KIPTableCard = (props: IKIPTableCardProps) => {
  const {
    style,
    className,
    titleEn,
    titleCn,
    height = 1000,
    columns = [],
    tableData = [],
    tabFilter,
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const title = getName(isEn, titleEn, titleCn);

  const divRef = useRef<HTMLDivElement>(null);

  const { scrollLeft } = useScrollbar(divRef, { horizontal: true });

  const renderMap = {
    // 游戏列render
    getGameRender: (rowData: any) => {
      const obj = {
        name: getName(isEn, rowData?.nameEn, rowData?.nameCn),
        img: rowData?.img,
      };
      return getGameRender(obj);
    },
    // 周数据列render
    getWeekRender: (rowData: any, col: IColumn) => {
      const {
        lastWeekKey,
        lastTwoWeekKey,
        lastWeekNameEn,
        lastWeekNameCn,
        lastTwoWeekNameEn,
        lastTwoWeekNameCn,
        kpiNameEn,
        kpiNameCn,
        lastWeekColor,
        lastTwoWeekColor,
        kpiColor,
        valueFormatRule,
        percentFormatRule,
        dateFormatRule
      } = col;

      // 获取周数据
      const {
        [`${lastWeekKey}_kpi`]: lastWeekKpi,
        [`${lastWeekKey}_value`]: lastWeekValue,
        [`${lastTwoWeekKey}_kpi`]: lastTwoWeekKpi,
        [`${lastTwoWeekKey}_value`]: lastTwoWeekValue,
        [`${lastWeekKey}_start_date`]: lastWeekStartDate,
        [`${lastWeekKey}_end_date`]: lastWeekEndDate,
        [`${lastTwoWeekKey}_start_date`]: lastTwoWeekStartDate,
        [`${lastTwoWeekKey}_end_date`]: lastTwoWeekEndDate,
      } = rowData || {};

      const lastWeekData = {
        name: getName(isEn, lastWeekNameEn, lastWeekNameCn),
        value: lastWeekValue,
        kpi: lastWeekKpi,
        color: lastWeekColor,
        startDate: lastWeekStartDate,
        endDate: lastWeekEndDate,
      };
      const lastTwoWeekData = {
        name: getName(isEn, lastTwoWeekNameEn, lastTwoWeekNameCn),
        value: lastTwoWeekValue,
        kpi: lastTwoWeekKpi,
        color: lastTwoWeekColor,
        startDate: lastTwoWeekStartDate,
        endDate: lastTwoWeekEndDate,
      };

      // kpi不展示进度条
      const kpiData = {
        name: getName(isEn, kpiNameEn, kpiNameCn),
        color: kpiColor,
        noProgress: true
      };

      return getWeekRender({
        data: [lastWeekData, lastTwoWeekData, kpiData],
        valueFormatRule,
        percentFormatRule,
        dateFormatRule
      });
    },
    // 完成度列render
    getCompletionRender: (rowData: any, col: IColumn) => {
      const { [`${col.key}_value`]: value = '', [`${col.key}_kpi`]: kpi = '' } = rowData || {};

      let percent: number | string = value / kpi;
      if (!kpi || !value) {
        percent = '';
      }

      return getCompletionRender({
        value: percent,
        formatRule: col.formatRule,
      });
    },
  };

  const newColumns = useMemo(() => {
    const { date } = tabFilter || {};
    return columns.map((item) => {
      const { nameCn, nameEn, render, ...other } = item || {};
      const header = dynamicString(getName(isEn, nameEn, nameCn), {
        year: moment(date)?.year(),
        quarter: `Q${moment(date)?.quarter()}`
      });
      return {
        ...other,
        header,
        render: typeof render === 'string' ? renderMap[render] : render,
      };
    });
  }, [columns, tabFilter, isEn]);

  return (
    <CardTitleBox
      title={title}
      className={classNames(
        'dbd-studio-home-kpi-table-card-container',
        { 'is-zh': !isEn },
        className,
      )}
      style={style}
      normalConfig={{
        isLoading: false,
        isError: false,
        isNoData: tableData?.length === 0,
        style: { height },
      }}
    >
      <div
        ref={divRef}
        className={classNames('dbd-studio-home-kpi-table-card-content', {
          'dbd-studio-home-kpi-table-card-scroll': scrollLeft > 0,
        })}
      >
        <table>
          <thead>
            <tr>
              {newColumns?.map((item, index) => {
                const { header, align = 'left', width = 'auto', className } = item || {};
                return (
                  <th
                    key={index}
                    align={align}
                    style={{
                      width: `${width}px`,
                    }}
                    className={className}
                  >
                    {header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {tableData?.map((item, index) => (
              <tr key={index}>
                {newColumns?.map((col, index) => {
                  const { render, key, align, className, formatRule } = col;
                  let value = item[key];
                  if (formatRule) {
                    value = dataFormat({ value, formatRule });
                  }
                  return (
                    <td key={index} align={align} className={className}>
                      {(render ? render(item, col) : value) || '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardTitleBox>
  );
};

export default KIPTableCard;
